import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useAudienceKindSupport } from './useAudienceKindSupport';
import ReminderPlanDialog from './ReminderPlanDialog';
import type { GroupDetail } from '../../../types/groups';

/**
 * The Broadcasts tab's entry point to reminder plans. A separate file, and the
 * dialog mounted only while open, so its state (the drafted copy, the times) is
 * built fresh from the trip's CURRENT dates every time — and so BroadcastsTab
 * itself grows by two lines, not a dialog.
 */
export default function ReminderPlanButton({
  group,
  onOpenOverview,
}: {
  group: GroupDetail;
  onOpenOverview?: () => void;
}) {
  const [open, setOpen] = useState(false);
  // The same one-per-session probe the dialog uses (shared cache entry). A
  // server that has PROVED it can't decide audiences at send time (prod, before
  // #394) makes the dialog a dead end, so don't offer it; until the probe has
  // answered the button stays available and the dialog says the same thing.
  const { supported, checked } = useAudienceKindSupport(group.id);
  const unavailable = checked && !supported;
  return (
    <Box sx={{ mt: 2 }}>
      <Button variant="outlined" disabled={unavailable} onClick={() => setOpen(true)}>Set up reminders</Button>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
        {unavailable
          ? "Reminder plans aren't available yet. You can still schedule a single send with “Send at…”."
          : 'A series of scheduled messages counted down from the trip date.'}
      </Typography>
      {open ? (
        <ReminderPlanDialog group={group} onClose={() => setOpen(false)} onOpenOverview={onOpenOverview} />
      ) : null}
    </Box>
  );
}
